import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ThemeName = "light" | "dark";
export type ThemePreference = ThemeName;

const THEME_STORAGE_KEY = "squeek.theme";

interface ThemeContextValue {
  theme: ThemeName;
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "dark",
  preference: "dark",
  setPreference: () => {},
});

function systemTheme(): ThemeName {
  if (typeof window === "undefined" || !window.matchMedia) return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";
}

function storedPreference(): ThemePreference | null {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}

export function applyTheme(theme: ThemeName): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  // A theme flip changes every colour token at once; without this, every
  // element with a colour transition would animate (and repaint) together.
  const switching = root.dataset.theme !== undefined && root.dataset.theme !== theme;
  if (switching) root.dataset.themeSwitching = "";
  root.dataset.theme = theme;
  root.classList.toggle("dark", theme === "dark");
  if (!switching || typeof window.requestAnimationFrame !== "function") return;
  // Re-enable transitions only after one frame has been styled without them.
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      delete root.dataset.themeSwitching;
    });
  });
}

export function ThemeProvider({
  defaultPreference,
  children,
}: {
  defaultPreference?: ThemePreference;
  children: ReactNode;
}) {
  const [preference, setStoredPreference] = useState<ThemePreference>(
    () => storedPreference() ?? defaultPreference ?? systemTheme(),
  );
  const theme = preference;


  useEffect(() => applyTheme(theme), [theme]);

  const setPreference = useCallback((next: ThemePreference) => {
    setStoredPreference(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // The active in-memory preference still applies when storage is blocked.
    }
  }, []);

  const value = useMemo(
    () => ({ theme, preference, setPreference }),
    [preference, setPreference, theme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
